import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../services/api';
import { waitForJob } from '../utils/waitForJob';
import PageHeader from '../components/ui/PageHeader';
import EpicClassificationPanel from '../components/categorisation/EpicClassificationPanel';
import { categoryPalette } from '../theme/tokens';

// Reuses the shared design-token palette rather than a local copy, so a new
// work type (e.g. Change) never silently falls back to an unstyled default
// here while being styled correctly everywhere else.
const WORK_TYPE_COLORS = Object.fromEntries(
  Object.entries(categoryPalette).map(([name, token]) => [name, token.main])
);

const Categorisation = () => {
  const [rules, setRules] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const loadRules = useCallback(async () => {
    const response = await api.get('/categorisation/rules');
    setRules(response.data.rules || []);
  }, []);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/categorisation/preview');
      setPreview(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to load categorisation preview.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRules().catch((err) => setError(err.message));
    loadPreview();
  }, [loadRules, loadPreview]);

  const applyRules = async () => {
    setApplying(true);
    setProgress(null);
    setError(null);
    setMessage(null);
    try {
      const started = await api.post('/categorisation/apply');
      const job = await waitForJob(api, started.data.jobId, {
        onProgress: setProgress,
      });
      setMessage(job.result?.message || 'Categorisation applied.');
      await loadPreview();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to apply categorisation.');
    } finally {
      setApplying(false);
    }
  };

  const summary = preview?.summary;

  return (
    <Box>
      <PageHeader
        title="Categorisation"
        subtitle="Review the rule path, inspect what will change, then apply it deliberately."
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h6">Workflow</Typography>
              <Typography variant="body2" color="text.secondary">
                Rules run top to bottom. A ticket is never silently turned into project work.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={loadPreview} disabled={loading || applying}>
                {loading ? 'Refreshing…' : 'Refresh preview'}
              </Button>
              <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={applyRules} disabled={loading || applying || !preview}>
                {applying ? 'Applying…' : 'Apply categorisation'}
              </Button>
            </Stack>
          </Stack>
          {applying && progress && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption">Processed {progress.processed} of {progress.total} · Updated {progress.updated}</Typography>
              <LinearProgress variant="determinate" value={progress.total ? (progress.processed / progress.total) * 100 : 0} sx={{ mt: 0.5 }} />
            </Box>
          )}
        </CardContent>
      </Card>

      <EpicClassificationPanel />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card><CardContent><Typography color="text.secondary">Tickets</Typography><Typography variant="h3">{summary?.total ?? '—'}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card><CardContent><Typography color="text.secondary">Would change</Typography><Typography variant="h3">{summary?.wouldChange ?? '—'}</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card><CardContent><Typography color="text.secondary">Needs review</Typography><Typography variant="h3" color={summary?.needsReview ? 'warning.main' : 'success.main'}>{summary?.needsReview ?? '—'}</Typography></CardContent></Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Decision path</Typography>
              <Stack divider={<Divider flexItem />}>
                {rules.map((rule) => (
                  <Stack key={rule.step} direction="row" spacing={2} sx={{ py: 1.5 }} alignItems="flex-start">
                    <Chip label={rule.order} size="small" color="primary" />
                    <Box>
                      <Typography variant="subtitle2">{rule.scope}: {rule.description}</Typography>
                      <Typography variant="caption" color="text.secondary">Result: {rule.workType}</Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Preview by work type</Typography>
              <Stack spacing={1}>
                {Object.entries(summary?.byWorkType || {}).map(([type, count]) => (
                  <Stack key={type} direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: WORK_TYPE_COLORS[type] || '#94A3B8' }} />
                      <Typography>{type}</Typography>
                    </Stack>
                    <Typography fontWeight={700}>{count}</Typography>
                  </Stack>
                ))}
                {!summary && <CircularProgress size={20} />}
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Review queue</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                These remain Unattributed until a rule or human decision resolves them.
              </Typography>
              <Table size="small">
                <TableHead><TableRow><TableCell>Ticket</TableCell><TableCell>Reason</TableCell></TableRow></TableHead>
                <TableBody>
                  {(preview?.reviewQueue || []).slice(0, 8).map((item) => (
                    <TableRow key={item.key}><TableCell>{item.key}</TableCell><TableCell>{item.reason}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
              {preview?.reviewQueueTruncated && <Typography variant="caption" color="text.secondary">Showing the first 8 of the review queue.</Typography>}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Categorisation;
